import { useCallback, useEffect, useState } from "react";

import UserAdd from "./UserAdd";
import UserProfiles from "./UserProfiles";
import UserAccessWorkspaces from "./UserAccessWorkspaces";

import { Heading } from "V2.0/common/heading";

import { useAccountContext } from "V2.0/Context/AccountContext";
import {
  addWhitelabelUser,
  deactivateUser as deactivate,
} from "V2.0/services/customerService";
import useCustomerProfiles from "V2.0/hooks/useCustomerProfiles";
import UsersModifyRoles from "./ModifyRoles";
import UserDeactivateConfirm from "./UserDeactivateConfirm";

const Action = {
  WorkspaceAccess: 0,
  DeactivateUser: 1,
  ModifyUserRole: 2,
} as const;

type Action = (typeof Action)[keyof typeof Action];

function UsersManagement() {
  const [open, setOpen] = useState({
    isOpen: false,
    user: null,
    action: null,
  });

  const { workspaces, company, syncWorkspace } = useAccountContext();

  const { customerProfiles, fetchCustomerProfiles, isCustomerProfilesLoading } =
    useCustomerProfiles(company?._id);

  const addUser = async (user, lifecycleCbs) => {
    lifecycleCbs.onInit();
    try {
      const response = await addWhitelabelUser({
        companyId: company?._id,
        payload: user,
      });
      if (response.status === 200) {
        fetchCustomerProfiles();
        lifecycleCbs.onSuccess();
        setOpen({
          isOpen: true,
          user,
          action: Action.WorkspaceAccess,
        });
      }
    } catch (e) {
      lifecycleCbs.onError(e);
    }
  };

  const deactivateUser = useCallback(async function (lifecycleCbs, user) {
    lifecycleCbs.init();

    try {
      const response = await deactivate({
        userId: user._id,
      });

      if (response.status === 200) {
        fetchCustomerProfiles();
        lifecycleCbs.success();
      }
    } catch (e) {
      lifecycleCbs.error(e);
    }
  }, []);

  const showAccessWorkspaces = workspaces.length > 0;

  return (
    <>
      <div className="w-4/5 mx-auto flex flex-col gap-y-8">
        <div className="flex justify-between">
          <div>
            <Heading>Team</Heading>
          </div>
          <UserAdd onCreate={addUser} />
        </div>
        <UserProfiles
          isLoading={isCustomerProfilesLoading}
          userProfiles={customerProfiles}
          onProfileClick={(user) =>
            setOpen({ isOpen: true, user, action: Action.WorkspaceAccess })
          }
          showAccessWorkspaces={showAccessWorkspaces}
          onModifyUserRole={(user) =>
            setOpen({ isOpen: true, user, action: Action.ModifyUserRole })
          }
          onDeactivateUser={(user) =>
            setOpen({ isOpen: true, user, action: Action.DeactivateUser })
          }
        />
      </div>
      {open.isOpen &&
        open.action === Action.WorkspaceAccess &&
        showAccessWorkspaces && (
          <UserAccessWorkspaces
            open={open.isOpen}
            onClose={() => setOpen({ isOpen: false, user: null, action: null })}
            user={open.user}
            workspaces={workspaces}
            syncWorkspace={syncWorkspace}
            company={company}
          />
        )}
      {open.isOpen && open.action === Action.ModifyUserRole && (
        <UsersModifyRoles
          user={open.user}
          onClose={() => setOpen({ isOpen: false, user: null, action: null })}
          fetchProfiles={fetchCustomerProfiles}
        />
      )}
      {open.isOpen && open.action === Action.DeactivateUser && (
        <UserDeactivateConfirm
          onCancel={() => setOpen({ isOpen: false, user: null, action: null })}
          user={open.user}
          onDeactivateUser={deactivateUser}
        />
      )}
    </>
  );
}

export default UsersManagement;
